'use strict';

/**
* @ngdoc controller
* @name tcApp.controller:TimelineItemCtrl
* @description
* Controller for generic graphical timeline items; previously graphCtrl.js
* 
* Current item service hooks:
* - getGetExtraActionMenuItems - returns a function that returns additional
*    action menu items for a given axis item
* - getActionMenuClickHandler - returns a function that handles click events
*    for action menu links
* - getVisibleHandler - returns a function that handles any additional model changes
*    caused by toggling an item's visibility
*/
angular.module('tcApp')
.controller('TimelineItemCtrl', function($window, $scope, $mdSidenav, $rootScope,
									GlobalService, TimelineService, GraphService, EventService, PathwayService, OverlayService) {
	// locals
	var self = this;
	var d3 = $window.window.d3;
	
	// Returns the service to use for a given timelineItem type
	var getTimelineItemService = function(profileType) {
		switch (profileType) {
			case TimelineService.GraphType:
				return GraphService;
			case TimelineService.EventTimelineType:
				return EventService;
			case TimelineService.PathwayTimelineType:
				return PathwayService;
			case TimelineService.OrderTimelineType:
				return EventService;
			default:
				return null;
		}
	};
	
	self.ItemService = getTimelineItemService($scope.data.profileType);
	
	// Calls the specified function of this item's service,
	// if it exists, otherwise returns the specified defaultReturn
	self.callItemService = function(axisGroup, functionName, defaultReturn) {
		if ((self.ItemService) && (self.ItemService[functionName])) {
			return self.ItemService[functionName]($scope, self, axisGroup);
		} else {
			return defaultReturn;
		}
	};
	
	var initialiseController = function() {
		$scope.viewModel = {
			lastInterval: $scope.tcTimeInterval, // used to differentiate between zoom/brush when timeFrom/To watch is triggered
			nonLinearRefRangeBaseTime: $scope.tcRefRangeBaseTime,
			listSettings: {},
			legendMarkerWidth: 50,
			legendMarkerHeight: 15,
			markerDefaultFillColor: '#ffffff',
			markerBaseSize: 4,
			transitionDuration: TimelineService.getSvgTransitionDuration(),
			noParent: angular.isUndefined($scope.tcXScale), // if true, graph does not have a timeline parent
			axisGroupModel: [],
			isStacked: TimelineService.isStacked($scope.data.profileType),
			timelinesShowing: TimelineService.timelinesShowing, 
			collapseID: $scope.getCollapseID()
		};
		if ($scope.data.labelCheckbox) {
			$scope.viewModel.showLabels = false; 
			if ($scope.data.showLabelsInitially) {
				$scope.viewModel.showLabels = $scope.data.showLabelsInitially;
			}
		}
		angular.forEach($scope.data.axisGroups, function(axisGroup, index) {
			$scope.viewModel.axisGroupModel.push({
				steps: axisGroup.steps
			});
		
			var isEventTimeline = $scope.data.profileType === TimelineService.EventTimelineType;
			self.initialiseGraphDimensions(axisGroup, index, isEventTimeline);
		});
	};
	
	// set svg dimensions for graph, called on init and resize
	this.initialiseGraphDimensions = function(axisGroup, index, isEventTimeline) {
		var axisGroupModel = $scope.viewModel.axisGroupModel[index];
		var initialGraphWidth = ($scope.tcGraphWidth ? $scope.tcGraphWidth : undefined);
		var last = (index === $scope.data.axisGroups.length - 1);
		axisGroupModel.dimensions = TimelineService.getTimelineDimensions(false, initialGraphWidth, $scope.data.profileType, last);
		if (isEventTimeline) {
			axisGroupModel.dimensions.height = axisGroup.steps * ($scope.viewModel.showLabels ? 32 : 20);
		} else {
			axisGroupModel.dimensions.height = axisGroup.steps * ($scope.viewModel.showLabels ? 30 : 15);
		}
		
		if (index === 0) {
			initialiseXAxis(); 
		}
		initialiseYAxes(axisGroup, index);
	};
	
	// setup d3 scale and axis for the x-axis based on current time data and dimensions
	var initialiseXAxis = function() {
		// Only necessary when the parent scope is not a timeline;
		// otherwise, the timeline itself will handle the scaling
		if ($scope.viewModel.noParent) {
			$scope.tcXScale = d3.scaleTime().rangeRound([0, $scope.viewModel.axisGroupModel[0].dimensions.width]);
			
			$scope.tcXAxis = d3.axisTop($scope.tcXScale);
			
			self.updateTimeWindow($scope.tcTimeFrom, $scope.tcTimeTo);
		}
	};
	
	// setup d3 scales and axes for left and right y-axes based on current dimensions and axis settings
	var initialiseYAxes = function(axisGroup, index) {
		var d3YScales = [];
		var d3YAxes = [];
		var axisGroupModel = $scope.viewModel.axisGroupModel[index];
		
		angular.forEach(axisGroup.yAxes, function(axis, axisIndex) {
			if (angular.isDefined(axis)) {
				// Defind scale and add to list of Scales
				var yScale = d3.scaleLinear().range([axisGroupModel.dimensions.height, 0])
					.domain([axis.axisMin, axis.axisMax]);
					
				d3YScales.push(yScale);
				
				// properties that vary from left to right axes
				var axisFunctions = [d3.axisLeft(), d3.axisRight()];
				var axisTickSizes = [-axisGroupModel.dimensions.width, 5];
				var yAxis = axisFunctions[axisIndex].scale(yScale)
					.tickSize(axisTickSizes[axisIndex]);
				// Add 1e-7 to axisMax to make range inclusive
				if (axis.axisStep !== 0) {
					var axisMinDecimalPlaces = 0;
					var axisMaxDecimalPlaces = 0;
					var axisStepDecimalPlaces = 0;
					
					if ((axis.axisMin % 1) != 0) {
						axisMinDecimalPlaces = axis.axisMin.toString().split('.')[1].length;
					} else {
						axisMinDecimalPlaces = 0;
					}
					
					if ((axis.axisMax % 1) != 0) {
						axisMaxDecimalPlaces = axis.axisMax.toString().split('.')[1].length;
					} else {
						axisMaxDecimalPlaces = 0;
					}
					
					if ((axis.axisStep % 1) != 0) {
						axisStepDecimalPlaces = axis.axisStep.toString().split('.')[1].length;
					} else {
						axisStepDecimalPlaces = 0;
					}
					
					var MaxDecimalPlace = Math.max(axisMinDecimalPlaces, axisMaxDecimalPlaces, axisStepDecimalPlaces);
						
					var formatstring = '.' + MaxDecimalPlace + 'f';
					
					if (axis.axisMin < axis.axisMax) {
						yAxis.tickValues(d3.range(axis.axisMin, (axis.axisMax + 1e-7), Math.abs(axis.axisStep)))
							.tickFormat(d3.format(formatstring));
					} else if (axis.axisMin > axis.axisMax) {
						yAxis.tickValues(d3.range(axis.axisMin, (axis.axisMax - 1e-7), -Math.abs(axis.axisStep)))
							.tickFormat(d3.format(formatstring));
					}
				}
				
				d3YAxes.push(yAxis);
			}
		});

		axisGroupModel.yScales = d3YScales;
		axisGroupModel.yAxes = d3YAxes;
	
	};
	
	// Sets domain for xScale, accounting for time direction
	this.updateTimeWindow = function(timeFrom, timeTo) {
		// Only necessary when the parent scope is not a timeline;
		// otherwise, the timeline itself will handle the scaling
		if (angular.isFunction($scope.tcXScale) && ($scope.viewModel.noParent)) {
			// Scale the range of the data, inverting for Right to Left time
			if ($scope.tcTimeDirection === 'RL') {
				$scope.tcXScale.domain([timeTo,timeFrom]);	
			} else {
				$scope.tcXScale.domain([timeFrom,timeTo]);
			}
			
			$scope.tcXAxis.ticks(TimelineService.getAxisTicks($scope.tcTimeInterval))
				.tickSize(-$scope.viewModel.dimensions.height);
		}	
	};
	
	// Determines if given index corresponds to the left axis in yAxes list
	this.isLeftAxis = function(axesIndex) {
		return axesIndex === 0;	
	};
	
	// Determines if given index corresponds to the right axis in yAxes list
	this.isRightAxis = function(axesIndex) {
		return axesIndex === 1;	
	};
	
	// returns xScale pixel coordinate from time
	this.getXCoordinateFromTime = function(time) {
		var xScale = $scope.tcXScale;
		if (angular.isFunction(xScale)) {
			return xScale(time);
		}
	};
	
	// for the specified axis, returns yScale pixel coordinate from data value
	this.getYCoordinateFromValue = function(value, axisGroupIndex, axesIndex) {
		var axisGroupModel = $scope.viewModel.axisGroupModel[axisGroupIndex];
		if (angular.isFunction(axisGroupModel.yScales[axesIndex])) {
			return axisGroupModel.yScales[axesIndex](value);
		} else {
			return 0;	
		}
	};
	
	// returns array of additional action menu items
	// called as getExtraActionMenuItems(axisItem)
	var getExtraActionMenuItems = self.callItemService(null, 'getGetExtraActionMenuItems', function() { return []; });
	
	// handles click of action menu links
	// called as actionMenuClickHandler(linkName, axisItem)
	var actionMenuClickHandler = self.callItemService(null, 'getActionMenuClickHandler', function() { return; });
	
	// get css class for legend container
	$scope.getLegendContainerClass = function() {
		return TimelineService.getLegendContainerClass();
	};
	
	$scope.getCollapseBarIcon = function(isCollapsed) { 
		return TimelineService.getCollapseBarIcon(isCollapsed);
	};
	
	// get css class for graph body container
	$scope.getGraphContainerClass = function() {
		return TimelineService.getTimeHeaderContainerClass($scope.tcShowLegend);
	};
	
	// Returns legend caption for a given item
	$scope.getLegendItemCaption = function(item) {
		return (item.caption + ((angular.isDefined(item.units) && item.units !== '') ? (', ' + item.units) : ''));
	};
	
	// TODO: consider storing the result of this somewhere?
	// Will need to do this if we want legend items to adjust themselves without serious performance issues
	//
	// Returns ordered list of legend items from both left and right axes
	$scope.getDefaultLegendItems = function() {
		var items = [];

		angular.forEach($scope.data.axisGroups, function(axisGroup) {
			angular.forEach(axisGroup.yAxes, function(axis) {
				if (angular.isDefined(axis)) {
					angular.forEach(axis.items, function(item) {
						items.push(item);	
					});
				}	
			});
		});

		items.sort(function(a, b) {
			return a.legendSequence - b.legendSequence;	
		});
		
		return items;
	};

	// Links event items and their visibility to their corresponding event category and the category visibility
	$scope.linkLegendCategoriesToItems = function() {
		var timelineItems = $scope.data.axisGroups[0].yAxes[0].items;
		var eventCategories = $scope.data.eventCategories;
		angular.forEach(eventCategories, function(category) {
			category.items = [];
			if (angular.isUndefined(category.visible)) {
				category.visible = true;
			}
		});
		angular.forEach(timelineItems, function(item) {
			if (item.eventCategory) {
				var category = eventCategories[item.eventCategory];
				category.items.push(item);
				item.visible = category.visible;
			}
		});
	};

	// Get legend items
	// called as LegendItemsHandler()
	$scope.LegendItemsHandler = self.callItemService(null, 'getLegendItemsHandler', $scope.getDefaultLegendItems);

	// Hide/show graph items when legend item checkbox is clicked
	// called as VisibleHandler()
	$scope.VisibleHandler = self.callItemService(null, 'getVisibleHandler', function() { return; });
	
	// Click handler for legend graph item action menu
	// Constructs action menu data for the associated axisItem,
	// then opens the action menu
	$scope.actionMenuHandler = function($event, axisItem) {
		if ($scope.data.profileType === TimelineService.EventTimelineType) {
			return;
		}

		$event.stopPropagation();
		
		var extraActionMenuItems = getExtraActionMenuItems(axisItem);
		
		var rowData = {
			'_actionHeaders': axisItem['actionMenuDataHeaders'],
			'_actionLinks': axisItem['actionMenuDataLinks'],
			'_actionItems': axisItem['actionMenuDataItems'].concat(extraActionMenuItems)
		};
		
		var rowIndex = 0;
		var actionMenuData = {
			componentHtmlName: $scope.htmlIdPrefix,
			listRow: rowData,
			translations: $scope.tcTranslations,
			rowIndex: rowIndex,
			triggeringElement: $event.currentTarget
		};
		// Set it twice to prevent flicker
		$scope.viewModel.listSettings.actionMenuRow = rowIndex;
		$mdSidenav('actionMenu').open().then(function actionMenuOpenHandler() {
			$scope.viewModel.listSettings.actionMenuRow = rowIndex;
			GlobalService.onModalToggle(true);
			$rootScope.$broadcast('System:focusActionMenu');
		});
		$mdSidenav('actionMenu').onClose(function actionMenuCloseHandler() {
			$scope.viewModel.listSettings.actionMenuRow = null;
		});
		$rootScope.$broadcast('System:updateActionMenu', actionMenuData);
	};
	
	// Catch Global:triggerEvent event broadcast by GlobalService
	// Used to detect clicks on action menu links
	$scope.$on('Global:triggerEvent', function(event, processEventOptions) {
		if (angular.isDefined(processEventOptions)
			&& (angular.isDefined(processEventOptions.componentHtmlName))
			&& (processEventOptions.componentHtmlName !== $scope.htmlIdPrefix)) {
			return;
		}
		
		// Find axis and graph item based on processEventOptions, then toggleEWS or Ref Ranges
		// this hasn't been fixed to work multiple axis groups
		var axis = $scope.data.axisGroups[0].yAxes[processEventOptions.eventItem.axesIndex];
		if (angular.isObject(axis)) {
			var item = axis.items[processEventOptions.eventItem.axisItemIndex];
			if (angular.isObject(item)) {
				actionMenuClickHandler(processEventOptions.eventItem.name, item);
			}
		}
	});
	
	// Toggle Legend ID
	$scope.getToggleLegendId = function() {
		return $scope.htmlIdPrefix + '-legend';
	};

	// Toggle the legend popover with icon information when clicking on the Legend link 
	$scope.toggleLegend = function($event) {
		var popoverComponent = 'system.Timeline.Legend.Popover';
		var popoverParams = $scope.data.popoverParams;
		var legendData = $scope.data.legendData; 
		if (angular.isUndefined(legendData)) return;
		var leftDefined = angular.isDefined(legendData.left);
		var rightDefined = angular.isDefined(legendData.right);
		if ((!leftDefined) &&  (!rightDefined)) return;	
		
		if (leftDefined && rightDefined) {
			// There is a right and a left column. Use the composite popover
			popoverComponent = 'system.Timeline.Legend.Popover.Composite';
			var legendDataRightJSON = encodeURIComponent(angular.toJson(legendData.right)); // Encode URI before passing it to popoverParams
			var legendDataLeftJSON  = encodeURIComponent(angular.toJson(legendData.left)); 
			popoverParams += '&LegendDataRight=' + legendDataRightJSON  + '&LegendDataLeft=' + legendDataLeftJSON;
		} else {
			// One column popover
			if (rightDefined) {
				legendData = legendData.right;
			} else if (leftDefined) {
				legendData = legendData.left;
			}
			var legendDataJSON = encodeURIComponent(angular.toJson(legendData)); // Encode URI before passing it to popoverParams
			popoverParams += '&LegendData=' + legendDataJSON;
		}

		$scope.$emit('Timeline:pauseAutoRefresh', {reason: 'Popover'});
		var overlayOptions = {
			event: $event,
			componentName: popoverComponent,
			params: popoverParams, 
			popoverFor: $scope.getToggleLegendId(),
			onRemovingHandler: function() {
				$scope.$emit('Timeline:playAutoRefresh', {reason: 'Popover'});
			}
		};
		OverlayService.open(overlayOptions);
	};

	// The ID of the timeline item corresponds to the $index in timeline.html ng-repeat
	$scope.getCollapseID = function() { 
		return $scope.htmlIdPrefix.split('tc_Timeline-item-')[1];
	};

	$scope.getItemLabel = function() { 
		return TimelineService.getItemLabel($scope.data);
	};

	// Toggle a specific timeline.
	$scope.toggleTimeline = function() { 
		$scope.data.collapsed = !$scope.data.collapsed;
	};

	// For graph and event items, we want to show it in the legend container if the container exists. (otherwise, showCollapseBarOutsideLegend will be true)
	$scope.showCollapseBarInLegend = function() { 
		return ((($scope.data.profileType === TimelineService.GraphType) 
			|| ($scope.data.profileType === TimelineService.EventTimelineType)) 
			&& $scope.tcShowLegend);
	};

	// Always show with pathway/order, show with graph/event if no legend
	$scope.showCollapseBarOutsideLegend = function() {
		return (($scope.data.profileType !== TimelineService.GraphType) 
			&& ($scope.data.profileType !== TimelineService.EventTimelineType)) 
			|| !$scope.tcShowLegend;
	};

	$scope.getMainGraphSVGId = function(index) {
		return ($scope.getAxisGroupHTMLId(index) + '-mainGraphSVG');
	};

	$scope.getTooltipId = function(index) {
		return ($scope.getAxisGroupHTMLId(index) + '-tooltip');
	};

	$scope.getAxisGroupHTMLId = function(index) {
		return ($scope.htmlIdPrefix + '_' + index);
	};

	initialiseController();
});
